/**
 * xux grid list widget
 */
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  difference,
  filter,
  get,
  includes,
  invoke,
  isEqual,
  isFunction,
  map,
  pick,
  set,
  uniqBy,
} from 'lodash';
import {
  Observable,
  observableCombineLatest,
  rx,
  Subject,
  Subscription,
} from '@shared/rx';
import {
  FilterModel,
  GRID_ROW_SELECTION,
  GridRowHeightName,
  GridView,
  IBaseEntityType,
  IFieldPreferences,
  IWidgetAction,
  IWidgetData,
  IXVIListColumn,
  TOOLBAR_BUTTON_POSITION,
  ToolbarButtonDefinition,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  XuxGridActionPayload,
  XuxGridOptions,
} from '@shared/types';
import {
  asObservable,
  isInfiniteRowModel,
  XuxGridActionButtonsHelper,
} from '@shared/utils';
import { DEFAULT_TEXT_FIELD, ENTITY_ID_PATH } from '../../../../config';
import { xviCustomGridComponents } from '../../xvi-grid/custom-components';
import { XuxGridComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components';
import { XVIError } from '@shared/models';
import { UiActions } from '@store/actions/ui';
import { InfiniteScrollDataSource } from '@shared/services/datasource/infinite-scroll-datasource';
import { BUTTON_ACTIONS } from '@text-content/button-templates';
import { onControlValueChanged } from '@shared/rx-operators';
import { updateGridSortFilter } from '@shared/helpers';
import { BehaviorSubject } from 'rxjs-compat';
import { getDisplayedItemsIds } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { WIDGETS_STATIC_TEXT_CONTENT } from '@app/i18n/constants/static-text-content.constants';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

type XuxGridOptionsWithActionButtonHandler = XuxGridOptions & {
  actionButtonHandler?: Function;
};

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-grid-list',
  templateUrl: './xux-grid-list-widget.component.html',
  styleUrls: ['./xux-grid-list-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxGridListComponent implements OnInit, OnDestroy {
  @Input() public field: IFieldPreferences;
  @ViewChild(XuxGridComponent, { static: false })
  public xuxGridComponent: XuxGridComponent;

  public selectOptionsObs: Observable<any[]>;
  public visibleItemCount$: BehaviorSubject<number> = new BehaviorSubject(0);

  public gridOptions: XuxGridOptionsWithActionButtonHandler = {
    orderControls: false,
    multiselect: true,
    animateRows: false,
    showSearch: true,
    showViewSelector: false,
    showPagination: false,
    valueField: ENTITY_ID_PATH,
    orderGhostLabelPath: DEFAULT_TEXT_FIELD,
    disableColumnSelector: true,
    hideActionPanel: false,
    treeViewMode: false,
    treeChildrenPropertyName: '',
    gridHeight: '100%',
    showGridSizeSelector: false,
    disableUserViews: true,
    gridRowHeight: GridRowHeightName.MEDIUM,
    searchPlaceholder: this.i18nService.instant(I18n.Common.Search),
    showPinnedRow: false,
    pinnedTopRowData: [],
    showRowOptions: false,
    rowSelection: GRID_ROW_SELECTION.MULTIPLE,
    inlineAdd: false,
    inlineAddButtonCaption: null,
    filterModelComposer: (): FilterModel => ({}),
    actionButtonHandler: (): void => {},
  };

  // By default there is an only column with item "text"
  public columns: IXVIListColumn[] = [
    {
      defaultView: true,
      path: 'text',
      title: this.i18nService.instant(I18n.Common.Text),
    },
  ];

  public actionButtons: ToolbarButtonDefinition[] = [];
  public availableActionButtons: ToolbarButtonDefinition[] = [];
  public rowActionButtons: ToolbarButtonDefinition[] = [];
  public gridView: GridView = {};
  public xviCustomGridComponents: object = xviCustomGridComponents;
  public isPending: boolean = false;
  public maxSelectLimit: number;
  public staticI18nTextContent = this.i18nService.translateStaticTextContent({
    xuxGrid: WIDGETS_STATIC_TEXT_CONTENT.xuxGrid,
  });

  protected subscriptions: Subscription[] = [];
  private returnFullData: boolean = true;
  private ignoreSelectionChanges: boolean = false;
  private selectedData: any[] = [];

  public constructor(
    public uiActions: UiActions,
    protected i18nService: XVII18nService,
  ) {}

  public ngOnInit(): void {
    const widgetData = this.field.widgetData;
    // pick all gridOptions values from widget data when available
    this.gridOptions = Object.assign(
      {},
      this.gridOptions,
      pick(widgetData, Object.keys(this.gridOptions)),
    );

    this.initInfiniteRowModel();

    this.maxSelectLimit = get(widgetData, 'maxSelectLimit');
    this.returnFullData = get(widgetData, 'returnFullData', false);
    this.columns = get(widgetData, 'columns', this.columns);
    this.visibleItemCount$ = get(
      widgetData,
      'displayCountObs',
      this.visibleItemCount$,
    );
    const selectOptions = get(widgetData, 'selectOptions', []);

    this.selectOptionsObs = observableCombineLatest([
      asObservable(selectOptions),
      onControlValueChanged(get(this.field, 'control')).pipe(rx.take(1)),
    ]).pipe(
      rx.map(([allOptions, controlValue]) =>
        isFunction(widgetData.mapper)
          ? widgetData.mapper(allOptions, controlValue)
          : allOptions,
      ),
    );

    this.subscriptions.push(
      this.field.control.valueChanges
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe((items) => {
          this.setSelectedNodes(items);
        }),
    );

    this.rowActionButtons = filter<ToolbarButtonDefinition>(
      get(widgetData, 'buttons', []),
      {
        position: TOOLBAR_BUTTON_POSITION.ROW,
      },
    );

    this.prepareActionButtons();
    this.updateActionButtons([]);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public onGridAction($event: XuxGridAction): void {
    const actionType: XUX_GRID_ACTIONS = get($event, 'type', null);
    const payload = $event.payload;

    if (this.field.action instanceof Subject) {
      const eventData: IWidgetAction = {
        ...$event,
        fieldPath: get(this, 'field.path'),
      };
      this.field.action.next(eventData);
    }

    invoke(this.field.widgetData, ['eventHandlers', actionType], $event, this);

    switch (actionType) {
      case XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED:
        this.updateActionButtons(payload.rowData);
        const isTreeMode = get(this.gridOptions, 'treeViewMode', false);
        const selectedData = isTreeMode
          ? this.getSelectedData(payload)
          : payload.rowData;
        this.selectedData = selectedData;
        this.setControlValue(selectedData);
        break;

      case XUX_GRID_ACTIONS.GRID_READY:
        this.setSelectedNodes(this.field.control.value);
        this.setModelSubtitleVisisble();

        const context = get(payload, [
          'gridApi',
          'gridOptionsWrapper',
          'gridOptions',
          'context',
        ]);

        if (context) {
          // update context
          set(context, 'isTreeViewMode', this.gridOptions?.treeViewMode);
        }
        break;

      case XUX_GRID_ACTIONS.LIST_VIEW_RESET:
        this.xuxGridComponent.emitAction({
          type: XUX_GRID_ACTIONS.LIST_GROUP_COLLAPSE_ALL,
        });
        this.xuxGridComponent.emitAction({
          type: XUX_GRID_ACTIONS.ROW_HEIGHT_CHANGED,
        });
        break;

      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
        this.resetDataSource();
        this.setModelSubtitleVisisble();
        break;

      case XUX_GRID_ACTIONS.ACTION_BUTTON_CLICKED:
        this.handleActionButtonClicked(payload);
        XuxGridActionButtonsHelper.invokeCustomClickHandler(
          this.actionButtons,
          payload.value,
          payload.rowData,
        );
        break;
      case XUX_GRID_ACTIONS.ADD_ROW:
        const updatedPayload = { value: BUTTON_ACTIONS.ADD_ITEMS, rowData: [] };
        this.handleActionButtonClicked(updatedPayload);
        XuxGridActionButtonsHelper.invokeCustomClickHandler(
          this.actionButtons,
          updatedPayload.value,
          updatedPayload.rowData,
        );
        break;
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        updateGridSortFilter(this.xuxGridComponent, payload.value?.trim());
        this.xuxGridComponent.gridApi.setQuickFilter(payload.value);
        this.xuxGridComponent.gridApi.resetRowHeights();
        this.setModelSubtitleVisisble();
        break;
      default:
        return null;
    }
  }

  public getSelectedData = (
    payload: XuxGridActionPayload,
  ): IBaseEntityType[] => {
    const removedItem = uniqBy(
      difference(this.selectedData, payload.rowData),
      this.gridOptions.valueField,
    );
    if (!removedItem.length) {
      return payload.rowData;
    }
    const removedItemIds = map(removedItem, this.gridOptions.valueField);

    return filter(
      payload.rowData,
      (item) =>
        !includes(removedItemIds, get(item, this.gridOptions.valueField)),
    );
  };

  public setSelectedNodes(controlArrayValue: object[] | string[] = []): void {
    const itemsValue = this.returnFullData
      ? map(controlArrayValue as object[], this.gridOptions.valueField)
      : controlArrayValue;

    if (
      this.xuxGridComponent &&
      !!this.xuxGridComponent['gridApi'] &&
      this.gridOptions
    ) {
      this.ignoreSelectionChanges = true;
      const valuesArrayData = map(itemsValue, this.getItemValue);
      this.xuxGridComponent['gridApi'].forEachNode((node) => {
        const entityId = get(node, `data.${this.gridOptions.valueField}`, '');
        // for returnFullData mode we compare entityId of items.
        const itemValue = this.returnFullData
          ? entityId
          : this.getItemValue(node.data);
        const selectNode: boolean = valuesArrayData.includes(itemValue);

        return node.id && node.setSelected(selectNode);
      });
      this.ignoreSelectionChanges = false;
    }
  }

  private setModelSubtitleVisisble(): void {
    const filterItems = getDisplayedItemsIds(this.xuxGridComponent.gridApi);
    this.visibleItemCount$.next(filterItems.length ?? 0);
  }

  private getItemValue = (item: object | string): string => {
    return get(item, this.gridOptions.valueField, item as string);
  };

  private getProvisionedArray(items: object[] | string[]): string[] {
    return map(items, this.getItemValue);
  }

  private setControlValue(items: object[]): void {
    if (this.ignoreSelectionChanges) return;
    const orderedItems = this.returnFullData
      ? items
      : this.getProvisionedArray(items);
    this.field.control.setValue(orderedItems);
    this.field.control.markAsDirty();
  }

  private initInfiniteRowModel(): void {
    if (
      !isInfiniteRowModel(this, 'field.widgetData.userGridOptions.rowModelType')
    ) {
      return;
    }

    const dataSource: InfiniteScrollDataSource = get(
      this,
      'field.widgetData.userGridOptions.datasource',
    );
    this.subscriptions.push(
      dataSource.isBusyObs().subscribe((isBusy) => (this.isPending = isBusy)),
      dataSource
        .getErrorObs()
        .subscribe((error: XVIError) =>
          this.uiActions.showErrorToasterMessage({ error }),
        ),
    );
  }

  private resetDataSource(): void {
    const dataSource: InfiniteScrollDataSource = get(
      this,
      'field.widgetData.userGridOptions.datasource',
    );
    if (!dataSource) {
      return;
    }

    dataSource.purge();
    this.xuxGridComponent.gridApi.setDatasource(dataSource);
  }

  private getWidgetData(): IWidgetData {
    return get(this, 'field.widgetData', {});
  }

  private prepareActionButtons(): void {
    this.availableActionButtons =
      XuxGridActionButtonsHelper.prepareActionButtons(
        this.getWidgetData(),
        true,
      );
  }

  private updateActionButtons(selectedItems: object[]): void {
    const buttons = XuxGridActionButtonsHelper.manageActionButtons(
      selectedItems,
      this.availableActionButtons,
      this.getWidgetData(),
    );
    this.actionButtons =
      XuxGridActionButtonsHelper.putButtonsActionToDropDown(buttons);
  }

  private handleActionButtonClicked(payload: XuxGridActionPayload): void {
    const widgetData = this.getWidgetData();
    const actionButtonHandler: Function = get(
      widgetData,
      'actionButtonHandler',
    );
    if (actionButtonHandler) {
      actionButtonHandler(payload);
    }
  }
}
